/**
 * Studio Series Mutation Hooks
 *
 * Cache invalidation uses prefix-matching: `['studio', 'series']` covers every
 * list/detail entry under `queryKeys.studio.series.*`. The atomic move
 * mutation invalidates BOTH source and target parent details — caller must
 * supply both UUIDs in variables.
 *
 * Consumers SHOULD destructure stable methods (`mutate`, `mutateAsync`,
 * `reset`) rather than depending on the whole mutation object in effect deps
 * — see CLAUDE.md "TanStack Query hook returns: destructure stable methods".
 *
 * Error envelopes per contract §10 are NOT swallowed here — let consumer
 * `onError` inspect `error.response.data.code` to dispatch dialogs
 * (e.g. `VIDEO_ALREADY_IN_SERIES` → MoveVideoConflictDialog).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { studioSeriesClient } from '../client/studio-series.client';
import { queryKeys } from '../query-keys';
import type {
  CreateStudioSeriesPayload,
  MoveVideoPayload,
  ReorderVideosPayload,
  ReorderVideosResponse,
  UpdateStudioSeriesPayload,
} from '@/types/studio-series';

const STUDIO_SERIES_PREFIX = ['studio', 'series'] as const;
const STUDIO_VIDEOS_PREFIX = ['studio', 'videos'] as const;

export function useCreateStudioSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStudioSeriesPayload) => studioSeriesClient.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

export function useUpdateStudioSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, payload }: { uuid: string; payload: UpdateStudioSeriesPayload }) =>
      studioSeriesClient.update(uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

export function useClearStudioSeriesThumbnail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.clearThumbnail(uuid),
    onSuccess: (_data, uuid) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.series.detail(uuid) });
    },
  });
}

export function useSubmitSeriesForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.submitForReview(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

export function useWithdrawSeriesSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.withdrawSubmission(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

export function useUnpublishSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.unpublish(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

export function usePublishSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.publish(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_SERIES_PREFIX });
    },
  });
}

/**
 * Detail entry is removed (not invalidated) so a mounted detail page doesn't
 * refetch a 404 after delete. Studio videos are invalidated because every
 * episode's `series_id` just went null.
 */
export function useDeleteSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioSeriesClient.remove(uuid),
    onSuccess: (_data, uuid) => {
      queryClient.removeQueries({ queryKey: queryKeys.studio.series.detail(uuid) });
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.series.lists() });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

export function useAttachVideoToSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesUuid, videoUuid }: { seriesUuid: string; videoUuid: string }) =>
      studioSeriesClient.attachVideo(seriesUuid, videoUuid),
    onSuccess: (_data, { seriesUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.series.detail(seriesUuid) });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

export function useDetachVideoFromSeries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesUuid, videoUuid }: { seriesUuid: string; videoUuid: string }) =>
      studioSeriesClient.detachVideo(seriesUuid, videoUuid),
    onSuccess: (_data, { seriesUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.series.detail(seriesUuid) });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

/**
 * Optimistic reorder. Patches `display_order` in the detail cache before the
 * round-trip; rolls back on error; re-sorts and invalidates on settle so the
 * canonical order from the server replaces the optimistic one.
 */
export function useReorderSeriesVideos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ seriesUuid, payload }: { seriesUuid: string; payload: ReorderVideosPayload }) =>
      studioSeriesClient.reorderVideos(seriesUuid, payload),
    onMutate: async ({ seriesUuid, payload }) => {
      const detailKey = queryKeys.studio.series.detail(seriesUuid);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData(detailKey);
      queryClient.setQueryData(detailKey, (current: unknown) => {
        if (!current || typeof current !== 'object' || !('videos' in current)) return current;
        const detail = current as { videos: { uuid: string; display_order: number }[] };
        const orderMap = new Map(payload.order.map((o) => [o.uuid, o.display_order]));
        const nextVideos = detail.videos
          .map((v) => ({ ...v, display_order: orderMap.get(v.uuid) ?? v.display_order }))
          .sort((a, b) => a.display_order - b.display_order);
        return { ...detail, videos: nextVideos };
      });
      return { previous, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.detailKey, context.previous);
      }
    },
    onSettled: (_data: ReorderVideosResponse | undefined, _err, { seriesUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.series.detail(seriesUuid) });
    },
  });
}

/**
 * Atomic cross-parent move (§5.13). Invalidates BOTH source and target parent
 * details + the studio videos cache (since `videos.series_id` changed).
 */
export function useMoveVideo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      videoUuid,
      payload,
    }: {
      videoUuid: string;
      sourceParentUuid: string;
      payload: MoveVideoPayload;
    }) => studioSeriesClient.moveVideo(videoUuid, payload),
    onSuccess: (_data, { sourceParentUuid, payload }) => {
      const sourceKey =
        sourceParentUuid && sourceParentUuid.length > 0
          ? queryKeys.studio.series.detail(sourceParentUuid)
          : null;
      const targetKey =
        payload.target_type === 'series'
          ? queryKeys.studio.series.detail(payload.target_uuid)
          : queryKeys.studio.courses.detail(payload.target_uuid);
      if (sourceKey) queryClient.invalidateQueries({ queryKey: sourceKey });
      queryClient.invalidateQueries({ queryKey: targetKey });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}
